import React, { useEffect, useState } from 'react';

import ChangelogItem from './ChangelogItem/ChangelogItem';
import { DataTypeIcon, Text } from '@/common/components';

import './ChangelogAll.css';

import { RefreshUniversal } from '@duxton-web/icons';
import { Button, Flex, theme, Timeline, Typography } from '@duxton-web/ui';
import { Link } from 'react-router-dom';

import { formatUTCStringToDateTimeString } from '@/utils/dateTime';

interface OwnProps {
  changelogs: any[];
  includeUnchanged?: boolean;
}

function ChangelogsWrapper({
  changelogs,
  includeUnchanged,
}: {
  changelogs: any[];
  includeUnchanged: boolean;
}) {
  if (!changelogs) return <></>;

  const items = [];

  changelogs.forEach((changelog) => {
    items.push(
      <ChangelogItem
        data-testid="changelog-all-changelogitem"
        changelog={changelog}
        renderDate={false}
        includeUnchanged={includeUnchanged}
      />,
    );
  });

  return <>{items}</>;
}

const VariableTimeline = ({
  variableChangelogsMap,
  includeUnchanged,
}: {
  variableChangelogsMap: Map<string, any[]>;
  includeUnchanged: boolean;
}) => {
  if (!variableChangelogsMap) return <></>;

  const items = [];

  variableChangelogsMap.forEach((changelogs: any[]) => {
    const variableType = changelogs?.[0]?.type;
    const name = changelogs?.[0]?.name;
    const variableDesc = changelogs?.[0]?.desc;

    items.push({
      children: (
        <Flex
          data-testid="changelogall-variable-timeline"
          vertical
          gap={8}
          style={{
            marginLeft: 8,
          }}
        >
          <Flex vertical>
            <Link
              className="changelog-all-variable-name"
              to={`/t/variables/${name}/rollout`}
            >
              {name}
            </Link>
            <Flex align="center" gap={4}>
              <Typography.Metadata
                style={{
                  color: theme.getDesignToken().grey7,
                  minWidth: 'fit-content',
                  width: 'fit-content',
                  fontSize: '10px',
                }}
                strong
              >
                {variableType.toUpperCase()}
              </Typography.Metadata>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="2"
                height="2"
                viewBox="0 0 2 2"
                fill="none"
              >
                <circle cx="1" cy="1" r="1" fill="#D9D9D9" />
              </svg>
              <Typography.Metadata
                style={{
                  flexGrow: '1',
                  color: theme.getDesignToken().grey7,
                }}
                ellipsis
              >
                {variableDesc}
              </Typography.Metadata>
            </Flex>
          </Flex>

          <ChangelogsWrapper
            changelogs={changelogs}
            includeUnchanged={includeUnchanged}
          />
        </Flex>
      ),
      dot: <DataTypeIcon dataType={variableType} />,
    });
  });

  return (
    <Timeline
      className="changelog-all-variables-timeline"
      style={{
        flexGrow: 1,
        overflow: 'hidden auto',
        paddingTop: '4px',
        padding: '0 16px 0 16px',
      }}
      mode="left"
      items={items}
    />
  );
};

const TimelineRenderer = ({ timelineMap, includeUnchanged }) => {
  if (!timelineMap) return <></>;

  const items = [];

  timelineMap.forEach((value, key: string) => {
    items.push({
      children: (
        <Flex
          vertical
          gap={8}
          style={{
            marginLeft: '8px',
          }}
        >
          <Text strong>{`Changes on ${key}`}</Text>

          <VariableTimeline
            variableChangelogsMap={value}
            includeUnchanged={includeUnchanged}
          />
        </Flex>
      ),
      dot: (
        <Button
          disabled
          shape="circle"
          size="small"
          style={{
            backgroundColor: '#FFF',
            border: '1px solid #3D3D3D',
          }}
          icon={
            <RefreshUniversal
              style={{
                color: '#1A1A1A',
              }}
            />
          }
        />
      ),
      position: 'right',
    });
  });

  return (
    <Timeline
      className="changelog-all"
      style={{
        flexGrow: 1,
        overflow: 'hidden auto',
        paddingTop: '4px',
        padding: '4px 16px 0 16px',
        paddingInlineStart: '24px',
      }}
      mode="left"
      items={items}
    />
  );
};

export default function ChangelogAll({
  changelogs,
  includeUnchanged,
}: OwnProps) {
  const [timelineMap, setTimelineMap] =
    useState<Map<string, Map<string, any[]>>>();

  function parseChangelogs(changelogs: any[]) {
    const newTimelineMap = new Map<string, Map<string, any[]>>();

    changelogs.forEach((changelog) => {
      const approvedAt = formatUTCStringToDateTimeString(changelog?.updated_at);
      const approvedAtDateString = approvedAt.split(',')?.[0];
      const variableName = changelog?.name;

      if (!approvedAtDateString) return;

      const variableChangelogsMap = newTimelineMap.get(approvedAtDateString);

      if (!variableChangelogsMap) {
        newTimelineMap.set(
          approvedAtDateString,
          new Map<string, any[]>().set(variableName, [changelog]),
        );

        return;
      }

      const variableChangelogs = variableChangelogsMap.get(variableName);

      if (!variableChangelogs) {
        variableChangelogsMap.set(variableName, [changelog]);

        return;
      }

      variableChangelogsMap.set(variableName, [
        ...variableChangelogs,
        ...[changelog],
      ]);
    });

    setTimelineMap(newTimelineMap);
  }

  useEffect(() => {
    if (!changelogs) setTimelineMap(new Map());

    parseChangelogs(changelogs);
  }, []);

  return (
    <TimelineRenderer
      timelineMap={timelineMap}
      includeUnchanged={includeUnchanged}
    />
  );
}
